import * as XLSX from 'xlsx';

/**
 * Export table data to Excel file
 * @param {Array} data - Array of data objects to export
 * @param {Array} columns - Array of column definitions with accessorKey and header
 * @param {String} filename - Name of the exported file (without extension)
 */
export const exportToExcel = (data, columns, filename = 'export') => {
  if (!data || data.length === 0) {
    return;
  }

  // Transform data based on columns
  const exportData = data.map(row => {
    const exportRow = {};
    columns.forEach(column => {
      // Skip columns without header (like avatar or action columns)
      if (!column.header || column.header === ' ' || column.id === 'actions' || column.id === 'avatar') {
        return;
      }

      const value = row[column.accessorKey];

      // Handle special data types
      if (column.id === 'phoneNumber' && value) {
        // Format phone number - just the number
        exportRow[column.header] = value.number || '';
      } else if ((column.id === 'facilities' || column.id === 'facility') && Array.isArray(value)) {
        // Format facilities array
        exportRow[column.header] = value.map(f => f.facilityName).join(', ');
      } else if (column.id === 'reviewers' && Array.isArray(value)) {
        // Format reviewers array (for reviewer groups)
        exportRow[column.header] = value.map(r => `${r.fullName} (${r.email})`).join('; ');
      } else if (column.id === 'level') {
        // Format level (replace underscore with space)
        exportRow[column.header] = value ? value.replace('_', ' ') : '';
      } else if (column.id === 'reviewerLevel' && value) {
        // Format reviewer level from reviewerFlow object
        exportRow[column.header] = value.reviewerFlow ? value.reviewerFlow.replace('_', ' ') : '';
      } else if (column.id === 'reviewersForLevel1' && value) {
        // Format reviewers for level 1 - extract fullName from each reviewer
        const reviewers = value.reviewersForLevel1;
        exportRow[column.header] = Array.isArray(reviewers) && reviewers.length > 0
          ? reviewers.map(r => r.fullName).join(', ')
          : '';
      } else if (column.id === 'biller' && value) {
        // Format biller - extract fullName
        exportRow[column.header] = value.fullName || '';
      } else if (column.id === 'createdAt' && value) {
        // Format createdAt as mm-dd-yyyy local time
        // Handle both seconds (10 digits) and milliseconds (13 digits) timestamps
        const timestamp = String(value).length <= 10 ? value * 1000 : value;
        const date = new Date(timestamp);
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const year = date.getFullYear();
        exportRow[column.header] = `${month}-${day}-${year}`;
      } else {
        // Default: use value as is
        exportRow[column.header] = value || '';
      }
    });
    return exportRow;
  });

  // Create workbook and worksheet
  const worksheet = XLSX.utils.json_to_sheet(exportData);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');

  // Auto-size columns
  const maxWidth = 50;
  const columnWidths = {};

  exportData.forEach(row => {
    Object.keys(row).forEach(key => {
      const value = String(row[key] || '');
      const length = value.length;
      columnWidths[key] = Math.max(
        columnWidths[key] || key.length,
        Math.min(length, maxWidth)
      );
    });
  });

  worksheet['!cols'] = Object.values(columnWidths).map(width => ({ wch: width + 2 }));

  // Generate and download file
  XLSX.writeFile(workbook, `${filename}.xlsx`);
};
